import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { LogOut, X, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { fetchLeads, fetchStats, updateLeadStatus } from "@/lib/api";
import AdminInsights from "@/pages/AdminInsights";

const STATUS_META = {
  new: { color: "text-cyan-precision", bg: "bg-cyan-precision/10 border-cyan-precision/30" },
  contacted: { color: "text-amber-300", bg: "bg-amber-300/10 border-amber-300/30" },
  closed: { color: "text-emerald-300", bg: "bg-emerald-300/10 border-emerald-300/30" },
};

function scoreColor(s) {
  if (s >= 60) return "text-cyan-precision";
  if (s >= 40) return "text-amber-300";
  return "text-[#94A3B8]";
}

export default function Admin() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const a = t.admin;
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(filter), fetchStats()]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      toast.error("Gagal memuat data.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateLeadStatus(id, status);
      toast.success(a.statusUpdated);
      setSelected(updated);
      load();
    } catch (e) {
      toast.error("Gagal memperbarui status.");
    }
  };

  const statusLabel = (s) => ({ new: a.statusNew, contacted: a.statusContacted, closed: a.statusClosed }[s] || s);

  const statCards = stats
    ? [
        { label: a.statTotal, value: stats.total },
        { label: a.statNew, value: stats.new },
        { label: a.statContacted, value: stats.contacted },
        { label: a.statClosed, value: stats.closed },
        { label: a.statHigh, value: stats.high_score, accent: true },
      ]
    : [];

  const filters = [
    { key: "all", label: a.filterAll },
    { key: "new", label: a.statusNew },
    { key: "contacted", label: a.statusContacted },
    { key: "closed", label: a.statusClosed },
  ];

  return (
    <div className="min-h-screen bg-navy text-[#F0F4F8]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-navy/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg width="22" height="22" viewBox="0 0 32 32" fill="none">
              <rect x="1" y="1" width="30" height="30" stroke="#00E5FF" strokeWidth="1.5" />
              <path d="M9 9v14h11" stroke="#00E5FF" strokeWidth="2" />
              <circle cx="22" cy="10" r="2.5" fill="#00E5FF" />
            </svg>
            <span className="font-display font-semibold tracking-tight">Litterantara</span>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-precision border border-cyan-precision/30 px-2 py-1 ml-2">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] hover:text-cyan-precision transition-colors"
            >
              <ArrowLeft size={13} /> Situs
            </Link>
            <span className="hidden md:block text-xs text-[#94A3B8]">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest border border-white/10 text-[#F0F4F8] px-3 py-2 hover:border-cyan-precision hover:text-cyan-precision transition-colors"
            >
              <LogOut size={13} /> {a.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1320px] mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-3xl font-light tracking-tight">{a.dashboardTitle}</h1>
        <p className="mt-2 text-[#94A3B8]">{a.dashboardSub}</p>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-white/10">
          {[
            { key: "leads", label: a.tabLeads },
            { key: "insights", label: a.tabInsights },
          ].map((tb) => (
            <button
              key={tb.key}
              data-testid={`tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-3 border-b-2 -mb-px transition-colors ${
                tab === tb.key
                  ? "border-cyan-precision text-cyan-precision"
                  : "border-transparent text-[#94A3B8] hover:text-[#F0F4F8]"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="mt-8">{tab === "insights" && <AdminInsights />}</div>

        {tab === "leads" && (
          <>
        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {statCards.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className={`border p-5 ${s.accent ? "border-cyan-precision/40 bg-cyan-precision/5" : "border-white/10 bg-navy-surface/40"}`}
            >
              <div className={`font-display text-4xl font-light ${s.accent ? "text-cyan-precision" : "text-[#F0F4F8]"}`}>
                {s.value}
              </div>
              <div className="mt-1 font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8]">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                filter === f.key
                  ? "border-cyan-precision text-cyan-precision bg-cyan-precision/5"
                  : "border-white/10 text-[#94A3B8] hover:text-[#F0F4F8]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {[a.colScore, a.colName, a.colCompany, a.colService, a.colStatus, a.colDate].map((h) => (
                  <th key={h} className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] px-5 py-4 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[#94A3B8]">…</td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[#94A3B8]" data-testid="leads-empty">
                    {a.empty}
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    data-testid={`lead-row-${lead.id}`}
                    onClick={() => setSelected(lead)}
                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  >
                    <td className={`px-5 py-4 font-display text-lg font-light ${scoreColor(lead.lead_score)}`}>
                      {lead.lead_score}
                    </td>
                    <td className="px-5 py-4 text-[#F0F4F8]">{lead.name}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{lead.company}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{lead.service_interest || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`font-mono-tech text-[10px] uppercase tracking-widest border px-2.5 py-1 ${STATUS_META[lead.status]?.bg} ${STATUS_META[lead.status]?.color}`}>
                        {statusLabel(lead.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[#94A3B8] whitespace-nowrap font-mono-tech text-xs">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
          </>
        )}
      </main>

      {/* Detail drawer */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            <motion.aside
              data-testid="lead-detail"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-navy-surface border-l border-white/10 z-50 overflow-y-auto"
            >
              <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl">{a.detailTitle}</h2>
                  <button
                    data-testid="close-detail"
                    onClick={() => setSelected(null)}
                    className="text-[#94A3B8] hover:text-[#F0F4F8]"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <div className={`font-display text-5xl font-light ${scoreColor(selected.lead_score)}`}>
                    {selected.lead_score}
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8]">
                    {a.colScore}
                  </div>
                </div>

                <dl className="space-y-4">
                  {[
                    [a.colName, selected.name],
                    ["Email", selected.email],
                    [a.colCompany, selected.company],
                    ["Telepon", selected.phone || "—"],
                    ["Industri", selected.industry || "—"],
                    ["Ukuran", selected.company_size || "—"],
                    [a.colService, selected.service_interest || "—"],
                    ["Anggaran", selected.budget || "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-3">
                      <dt className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] pt-1">{k}</dt>
                      <dd className="text-sm text-[#F0F4F8] text-right break-all">{v}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">Pesan</div>
                  <p className="text-sm text-[#F0F4F8]/85 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                </div>

                <div className="mt-8">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] mb-3">
                    {a.updateStatus}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["new", "contacted", "closed"].map((s) => (
                      <button
                        key={s}
                        data-testid={`set-status-${s}`}
                        onClick={() => changeStatus(selected.id, s)}
                        className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                          selected.status === s
                            ? `${STATUS_META[s].bg} ${STATUS_META[s].color}`
                            : "border-white/10 text-[#94A3B8] hover:text-[#F0F4F8]"
                        }`}
                      >
                        {statusLabel(s)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
